import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    CalendarDaysIcon,
    TruckIcon,
    ClockIcon,
    XCircleIcon,
    ChartBarIcon,
    ExclamationCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getTripAnalytics, getCancellationAnalytics } from "../api/analyticsApi";
import type { AnalyticsDateRange } from "../types";

// ============================================================================
// Helpers
// ============================================================================

const fmtNumber = (n: number | null | undefined) => {
    if (n == null) return "—";
    return new Intl.NumberFormat("en-NG").format(n);
};

// ============================================================================
// Component
// ============================================================================

export const TripAnalyticsRoute = () => {
    const [dateRange, setDateRange] = useState<AnalyticsDateRange>({});

    const {
        data: tripData,
        isLoading: tripLoading,
        error: tripError,
        refetch,
        isFetching,
    } = useQuery({
        queryKey: ["analytics-trips", dateRange],
        queryFn: () => getTripAnalytics(dateRange),
    });

    const {
        data: cancelData,
        isLoading: cancelLoading,
        error: cancelError,
    } = useQuery({
        queryKey: ["analytics-cancellations", dateRange],
        queryFn: () => getCancellationAnalytics(dateRange),
    });

    const trips = tripData?.data;
    const cancel = cancelData?.data;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (tripLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (tripError) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">
                    Failed to load trip analytics
                </h3>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Date Range */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                    <input
                        type="date"
                        value={dateRange.startDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, startDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <span className="text-sm text-slate-400">to</span>
                    <input
                        type="date"
                        value={dateRange.endDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, endDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {/* Trip Summary Cards */}
            {trips?.summary && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex items-center gap-3">
                            <div className="rounded-xl bg-blue-50 p-2 dark:bg-blue-900/20">
                                <TruckIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Total Trips</p>
                                <p className="text-xl font-bold text-slate-900 dark:text-white">
                                    {fmtNumber(trips.summary.totalTrips)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Completion Rate</p>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                            {trips.summary.completionRate?.toFixed(1)}%
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                            {fmtNumber(trips.summary.completedTrips)} completed ·{" "}
                            {fmtNumber(trips.summary.cancelledTrips)} cancelled
                        </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex items-center gap-3">
                            <div className="rounded-xl bg-amber-50 p-2 dark:bg-amber-900/20">
                                <ClockIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Avg Duration</p>
                                <p className="text-xl font-bold text-slate-900 dark:text-white">
                                    {trips.summary.averageDuration?.toFixed(1)} min
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Avg Distance / Wait</p>
                        <p className="text-xl font-bold text-slate-900 dark:text-white">
                            {trips.summary.averageDistance?.toFixed(1)} km
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                            Wait: {trips.summary.averageWaitTime?.toFixed(1)} min
                        </p>
                    </div>
                </div>
            )}

            {/* Trips by Type */}
            {trips?.tripsByType && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Trips by Type
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-3">
                        {Object.entries(trips.tripsByType).map(([type, count]) => (
                            <div
                                key={type}
                                className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50"
                            >
                                <p className="text-xs text-slate-500 dark:text-slate-400">{type}</p>
                                <p className="text-lg font-bold text-slate-900 dark:text-white">
                                    {fmtNumber(count)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Peak Hours */}
            {trips?.peakHours && trips.peakHours.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ClockIcon className="h-4 w-4" /> Peak Hours
                    </h3>
                    <div className="space-y-2">
                        {trips.peakHours.map((ph) => {
                            const max = Math.max(...trips.peakHours.map((x) => x.tripCount), 1);
                            const pct = (ph.tripCount / max) * 100;
                            return (
                                <div key={ph.hour} className="flex items-center gap-3">
                                    <span className="w-28 text-sm text-slate-700 dark:text-slate-300">
                                        {ph.label}
                                    </span>
                                    <div className="flex-1">
                                        <div className="h-4 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                            <div
                                                className="h-full rounded-full bg-blue-500"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                    <span className="w-16 text-right text-sm font-medium text-slate-900 dark:text-white">
                                        {fmtNumber(ph.tripCount)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Daily Trend */}
            {trips?.dailyTrend && trips.dailyTrend.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ChartBarIcon className="h-4 w-4" /> Daily Trip Trend
                    </h3>

                    <div className="mb-4 flex items-end gap-1" style={{ height: 100 }}>
                        {trips.dailyTrend.map((day) => {
                            const max = Math.max(...trips.dailyTrend.map((d) => d.total), 1);
                            const pct = (day.total / max) * 100;
                            return (
                                <div
                                    key={day.date}
                                    className="group relative flex-1"
                                    title={`${day.date}: ${day.total} total, ${day.completed} completed (${day.completionRate?.toFixed(1)}%)`}
                                >
                                    <div
                                        className="w-full rounded-t bg-gradient-to-t from-blue-600 to-blue-400 transition-all group-hover:from-blue-700 group-hover:to-blue-500"
                                        style={{ height: `${pct}%`, minHeight: day.total ? 4 : 0 }}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    <div className="max-h-64 overflow-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Date</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Total</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Completed</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Cancelled</th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Rate</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {trips.dailyTrend.map((day) => (
                                    <tr key={day.date}>
                                        <td className="px-3 py-2 text-slate-900 dark:text-white">{day.date}</td>
                                        <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{fmtNumber(day.total)}</td>
                                        <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400">{fmtNumber(day.completed)}</td>
                                        <td className="px-3 py-2 text-rose-600 dark:text-rose-400">{fmtNumber(day.cancelled)}</td>
                                        <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{day.completionRate?.toFixed(1)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ================================================================
                Cancellation Analytics
               ================================================================ */}
            <div className="border-t border-slate-200 pt-6 dark:border-slate-700">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
                    <XCircleIcon className="h-5 w-5 text-rose-500" /> Cancellation Analytics
                </h2>

                {cancelLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                    </div>
                ) : cancelError ? (
                    <p className="text-sm text-rose-500">Failed to load cancellation data</p>
                ) : cancel ? (
                    <div className="space-y-5">
                        {/* Cancel summary */}
                        {cancel.summary && (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Total</p>
                                    <p className="text-xl font-bold text-rose-600 dark:text-rose-400">
                                        {fmtNumber(cancel.summary.totalCancellations)}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">By Riders</p>
                                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                                        {fmtNumber(cancel.summary.riderCancellations)}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">By Drivers</p>
                                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                                        {fmtNumber(cancel.summary.driverCancellations)}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">System</p>
                                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                                        {fmtNumber(cancel.summary.systemCancellations)}
                                    </p>
                                </div>
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Cancel Rate</p>
                                    <p className="text-xl font-bold text-rose-600 dark:text-rose-400">
                                        {cancel.summary.cancellationRate?.toFixed(1)}%
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Reasons */}
                        {cancel.cancellationReasons && cancel.cancellationReasons.length > 0 && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                    <ExclamationCircleIcon className="h-4 w-4" /> Cancellation Reasons
                                </h3>
                                <div className="space-y-2">
                                    {cancel.cancellationReasons.map((r) => (
                                        <div key={r.reason} className="flex items-center gap-3">
                                            <span className="w-48 truncate text-sm text-slate-700 dark:text-slate-300">
                                                {r.reason}
                                            </span>
                                            <div className="flex-1">
                                                <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                                    <div
                                                        className="h-full rounded-full bg-rose-400"
                                                        style={{ width: `${r.percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                            <span className="w-20 text-right text-sm text-slate-500 dark:text-slate-400">
                                                {fmtNumber(r.count)} ({r.percentage?.toFixed(1)}%)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Time to pickup */}
                        {cancel.cancellationByTimeToPickup && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                    Cancellations by Time to Pickup
                                </h3>
                                <div className="grid gap-3 sm:grid-cols-4">
                                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">&lt; 2 min</p>
                                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                                            {fmtNumber(cancel.cancellationByTimeToPickup.under2min)}
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">2–5 min</p>
                                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                                            {fmtNumber(cancel.cancellationByTimeToPickup["2to5min"])}
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">5–10 min</p>
                                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                                            {fmtNumber(cancel.cancellationByTimeToPickup["5to10min"])}
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">&gt; 10 min</p>
                                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                                            {fmtNumber(cancel.cancellationByTimeToPickup.over10min)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Top Cancellers */}
                        <div className="grid gap-6 lg:grid-cols-2">
                            {cancel.topCancellingRiders && cancel.topCancellingRiders.length > 0 && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                    <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        Top Cancelling Riders
                                    </h3>
                                    <div className="space-y-2">
                                        {cancel.topCancellingRiders.map((u) => (
                                            <div
                                                key={u.userId}
                                                className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/50"
                                            >
                                                <span className="text-sm text-slate-900 dark:text-white">
                                                    {u.name}
                                                </span>
                                                <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
                                                    {u.cancellations} cancels
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {cancel.topCancellingDrivers && cancel.topCancellingDrivers.length > 0 && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                                    <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        Top Cancelling Drivers
                                    </h3>
                                    <div className="space-y-2">
                                        {cancel.topCancellingDrivers.map((u) => (
                                            <div
                                                key={u.userId}
                                                className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/50"
                                            >
                                                <span className="text-sm text-slate-900 dark:text-white">
                                                    {u.name}
                                                </span>
                                                <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
                                                    {u.cancellations} cancels
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Date range */}
            {trips?.dateRange && (
                <p className="text-right text-xs text-slate-400 dark:text-slate-500">
                    Period: {trips.dateRange.startDate} — {trips.dateRange.endDate}
                </p>
            )}
        </div>
    );
};
